<template>
  <el-dialog
    class="dialog-inner-scroller"
    :model-value="visible"
    :before-close="() => {
      emit('close');
    }"
    width="500"
  >
    <template #header>
      <div class="my-header">
        <h4 class="px-2 pb-3 text-xl text-[#344767] font-semibold">
          提现
        </h4>
      </div>
    </template>

    <div class="px-2 pb-3">
      <el-form
        ref="formRef"
        require-asterisk-position="right"
        label-position="top"
        :model="formValues"
        :rules="formRules"
      >
        <el-form-item prop="withdrawAccount" label="提现账号">
          <el-select
            v-model="formValues.withdrawAccount"
            placeholder="请选择"
            clearable
            filterable
            @keyup.enter="onConfirm"
          >
            <el-option
              v-for="item in banks"
              :key="item.withdrawAccount"
              :label="item.withdrawAccount"
              :value="item.withdrawAccount"
            />
          </el-select>
        </el-form-item>
        <el-form-item prop="withdrawMoney" label="提现金额（元）">
          <el-input v-model="formValues.withdrawMoney" autocomplete="new-password" placeholder="请输入" @keyup.enter="onConfirm" />
        </el-form-item>
        <el-form-item prop="payPassword" label="提现密码">
          <el-input v-model="formValues.payPassword" autocomplete="new-password" type="password" placeholder="请输入" @keyup.enter="onConfirm" />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input :model-value="props.payPhone" disabled />
        </el-form-item>
        <el-form-item prop="code" class="relative" label="验证码">
          <el-input
            v-model="formValues.code"
            class="w-[314px] relative box-border pl36"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            @keyup.enter="onConfirm"
          >
            <template #prefix>
              <svg-icon class="left-[10px] !w-[18px]" icon="lucide:shield-check" />
            </template>
          </el-input>

          <sms-button
            ref="smsBtnRef"
            class="relative h-[31px] !left-auto ml-[16px]"
            @click="doSendSms"
          />
        </el-form-item>
      </el-form>
    </div>

    <template #footer>
      <div class="flex justify-center">
        <el-button @click="emit('close')">
          取消
        </el-button>
        <el-button type="primary" :loading="loading" @click="onConfirm">
          确定
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { checkNumber, checkSmsCode, scrollToErrorField } from '@core/tools';
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { nextTick, PropType, ref, toRaw, watch } from 'vue';
import { SmsButton, SvgIcon } from '@vue3/components';
import { ESmsType, sendSmscode } from '@core/api';
import { queryZsWithdrawAccounts } from '@core/main/api';
import { IFinanceWallet, IZsWithdrawAccount, IZsWithdrawParams } from '@core/main/types';

let sendSmsLock = false;

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object as PropType<IFinanceWallet>,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  term: {
    type: String as PropType<'server' | 'company' | 'agent' | 'saas'>,
    default: 'server',
  },
  payPhone: {
    type: String,
    default: '',
  },
});

const smsBtnRef = ref<InstanceType<typeof SmsButton>>(null);
const formRef = ref<FormInstance>();
const formValues = ref({} as IZsWithdrawParams);
const banks = ref<Array<IZsWithdrawAccount>>([]);

const formRules = ref<FormRules<any>>({
  withdrawAccount: [
    {
      required: true,
      message: '请选择提现账号',
    },
  ],
  withdrawMoney: [
    {
      required: true,
      message: '请输入提现金额',
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (checkNumber(String(formValues.value.withdrawMoney), '提现金额', 2)) {
          callback(checkNumber(String(formValues.value.withdrawMoney), '提现金额', 2));
        }
        callback();
      },
    },
  ],
  payPassword: [
    {
      required: true,
      message: '请输入提现密码',
    },
  ],
  code: [
    {
      required: true,
      message: '请输入验证码',
    },
    {
      required: true,
      validator: (rule: any, value: string, callback: any) => {
        if (checkSmsCode(String(formValues.value.code))) {
          callback(checkSmsCode(String(formValues.value.code)));
        }
        callback();
      },
    },
  ],
});

async function doSendSms() {
  if (sendSmsLock) return;

  if (!props.payPhone) {
    ElMessage.error('获取手机号失败');
    return;
  }

  try {
    sendSmsLock = true;
    smsBtnRef.value?.showLoading();

    await sendSmscode(ESmsType.SplitBill, props.payPhone);

    smsBtnRef.value?.countdown();
  } catch (error) {
    smsBtnRef.value?.hideLoading();
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  setTimeout(() => {
    sendSmsLock = false;
  }, 1300);
}

function onConfirm() {
  formRef.value?.validate(async (valid) => {
    if (valid) {
      const unitId = banks.value.find((item) => item.withdrawAccount === formValues.value.withdrawAccount)?.unitId;

      emit('confirm', {
        ...toRaw(formValues.value),
        payPhone: props.payPhone,
        unitId,
      } as IZsWithdrawParams);
    } else {
      nextTick(() => {
        scrollToErrorField(formRef.value?.$el, '.el-dialog__body');
      });
    }
  });
}

async function fetchBanks() {
  try {
    const res = await queryZsWithdrawAccounts(props.data?.accountId);

    banks.value = res;
  } catch (error) {
    // 不处理
  }
}

function resetForm() {
  for (const key in formValues.value) {
    formValues.value[key] = undefined;
  }
}

watch(
  [
    () => props.data?.accountId,
    () => props.visible,
  ],
  ([id, visible]) => {
    if (!id || !visible) return;

    fetchBanks();
  },
  { immediate: true },
);

watch(
  () => props.visible,
  (val) => {
    if (val) {
      resetForm();
      nextTick(() => {
        formRef.value?.resetFields();
      });
    }
  },
);
</script>
